import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import PetCustomizer from './PetCustomizer';
import { setCustomizedProduct, setPetType } from '../../store/customizerSlice';
import { Oval } from 'react-loader-spinner';
import { toast } from 'react-toastify';

/**
 * APICustomizerWrapper.jsx
 * 
 * Wraps the customizer when loaded in API/iframe mode from third-party e-commerce sites.
 * 
 * URL params expected:
 * - apiMode=true: Indicates this is API mode
 * - uuid: Session UUID created by SDK
 * - platform: e-commerce platform (shopify, woocommerce, etc)
 * - productId: External product ID (e.g., Shopify product ID)
 * - hostname: Sales channel hostname (e.g., store.myshopify.com)
 * - petTypes: Comma-separated list of allowed pet types (e.g., "dog" or "dog,cat")
 * 
 * Flow:
 * 1. Extract URL parameters
 * 2. Fetch personalization session data from edge function
 * 3. Load TreatInk product data
 * 4. Pre-populate customizer with product
 * 5. Render PetCustomizer with apiMode=true
 * 6. Listen for postMessage from customizer
 */

const APICustomizerWrapper = ({ isMobile }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [sessionUuid, setSessionUuid] = useState(null);
  const [allowedPetTypes, setAllowedPetTypes] = useState(['Dog', 'Cat']);

  // Get Supabase URL from environment or config
  const supabaseUrl = 'https://api.treatink.com';

  useEffect(() => {
    const loadSessionData = async () => {
      try {
        // Extract URL parameters
        const uuid = searchParams.get('uuid');
        const platform = searchParams.get('platform');
        const productId = searchParams.get('productId');
        const hostname = searchParams.get('hostname');
        const petTypesParam = searchParams.get('petTypes');

        console.log('API Customizer initializing with params:', {
          uuid,
          platform,
          productId,
          hostname,
          petTypes: petTypesParam,
        });

        // Parse petTypes param - convert to capitalized format for display
        if (petTypesParam) {
          const types = petTypesParam.split(',').map(t => 
            t.trim().charAt(0).toUpperCase() + t.trim().slice(1).toLowerCase()
          );
          setAllowedPetTypes(types);
          console.log('Allowed pet types:', types);
        }

        // Verify we have required parameters
        if (!uuid) {
          throw new Error('Missing session UUID parameter');
        }

        if (!platform || !productId || !hostname) {
          throw new Error('Missing required API parameters (platform, productId, or hostname)');
        }

        setSessionUuid(uuid);

        // Fetch session data from edge function
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-personalization-session/${uuid}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || `Failed to fetch session: ${response.statusText}`
          );
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || 'Failed to load personalization session');
        }

        console.log('Session data loaded:', data.session);

        setSessionData(data.session);
        setProductData(data.product);

        // Pre-populate Redux with product data so customizer knows what to personalize
        if (data.product) {
          dispatch(
            setCustomizedProduct({
              product: {
                ID: data.product.id,
                title: data.product.title,
                SKU: data.product.sku,
                animalType: data.product.animal_type,
                imageUrl: data.product.image_url,
                price: data.product.price,
              },
              isSubscribed: false,
              quantity: 1,
            })
          );

          // Auto-select pet type from product if available
          if (data.product.animal_type) {
            dispatch(setPetType(data.product.animal_type));
          }
        }

        // Auto-select pet type if only one type is allowed
        const finalAllowedTypes = petTypesParam 
          ? petTypesParam.split(',').map(t => t.trim().charAt(0).toUpperCase() + t.trim().slice(1).toLowerCase())
          : ['Dog', 'Cat'];
        
        if (finalAllowedTypes.length === 1) {
          dispatch(setPetType(finalAllowedTypes[0]));
          console.log('Auto-selected pet type:', finalAllowedTypes[0]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading API session:', err);
        setError(err.message);
        toast.error('Failed to load customizer: ' + err.message);
        setLoading(false);
      }
    };

    loadSessionData();
  }, [searchParams, dispatch, supabaseUrl]);

  // Listen for messages from customizer (in case it needs to communicate back)
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('APICustomizerWrapper received message:', event.data);

      // Handle specific message types if needed
      if (event.data.type === 'treatink_close_modal') {
        // This shouldn't happen in iframe context, but could be for future use
        console.log('Close modal message received');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (loading) {
    return (
      <div className="api-customizer-loading flex flex-col items-center justify-center gap-4 py-20">
        <Oval height={50} width={50} color="#EA8000" secondaryColor="#e2e6ff" />
        <p className="text-center">Loading customizer...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="api-customizer-error flex flex-col items-center justify-center gap-4 py-20">
        <p className="text-center text-red-600 font-semibold">Error loading customizer</p>
        <p className="text-center text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="api-customizer-wrapper">
      <PetCustomizer
        apiMode={true}
        sessionUuid={sessionUuid}
        supabaseUrl={supabaseUrl}
        allowedPetTypes={allowedPetTypes}
      />
    </div>
  );
};

export default APICustomizerWrapper;
